/* eslint camelcase: ["error", {allow: ["is_locked", "default_max_distance", "default_max_weight", "default_max_volume", "default_time_window", "default_provides"]}] */
import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import { ContextualMenu } from '../../../components/menus/ContextualMenu';
import { ConfirmDeleteDialog } from '../../../dialogs/ConfirmDeleteDialog';
import { GeoFence } from '../../../domain/geoFence/GeoFence';
import { useDeleteProjectGeoFences } from '../../../hooks/project/useDeleteProjectGeoFences';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useUpdateProjectGeoFences } from '../../../hooks/project/useUpdateProjectGeoFences';
import { useRetrieveRightPanelInfo } from '../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { GeoFenceMapper } from '../../../mappers/geoFence/GeoFenceMapper';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { MixPanelEvents } from '../../../services/mixpanel';

type PlannerGeoFencesActionsMenuContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type PlannerGeoFencesActionsMenuType = `contextual` | `popover`;

type PlannerGeoFencesActionsMenuProps = {
  geoFences: GeoFence[];
  type?: PlannerGeoFencesActionsMenuType;
  contextAnchor?: PlannerGeoFencesActionsMenuContextAnchor;
  setContextAnchor?: (context?: PlannerGeoFencesActionsMenuContextAnchor) => void;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  showEdit?: boolean;
  showViewDetails?: boolean;
  showExport?: boolean;
  showEnable?: boolean;
  showDisable?: boolean;
  showDelete?: boolean;
  onEditGeoFence?: (geoFence: GeoFence) => void;
  onDeleteSuccess?: () => void;
};

export const PlannerGeoFencesActionsMenu = ({
  type = `contextual` as PlannerGeoFencesActionsMenuType,
  geoFences,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  setContextAnchor = () => {},
  popoverProps,
  showEdit = true,
  showViewDetails = true,
  showExport = false, // THIS IS TEMPORAL UNTIL WE KNOW HOW TO EXPORT GEOFENCES
  showEnable = true,
  showDisable = true,
  showDelete = true,
  onEditGeoFence,
  onDeleteSuccess,
}: PlannerGeoFencesActionsMenuProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { run: updateGeoFences, success: updateGeoFencesSuccess } = useUpdateProjectGeoFences();
  const { run: deleteGeoFences, success: deleteGeoFencesSuccess } = useDeleteProjectGeoFences({
    project: activeProject!,
  });

  const [dialog, setDialog] = useState<`export` | `delete` | undefined>(undefined);

  useEffect(() => {
    if (updateGeoFencesSuccess || deleteGeoFencesSuccess) {
      if (setContextAnchor) {
        setContextAnchor(undefined);
      }
      if (onDeleteSuccess && deleteGeoFencesSuccess) {
        onDeleteSuccess();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateGeoFencesSuccess, deleteGeoFencesSuccess]);

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="routes-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showEdit || geoFences.length !== 1
                ? []
                : [
                    {
                      key: `edit`,
                      width: 240,
                      onClick: () => {
                        if (onEditGeoFence) {
                          onEditGeoFence(geoFences[0]);
                          eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.edit);
                        }
                      },
                      content: <Text intlId="geo_fences.actions.edit" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showViewDetails || geoFences.length !== 1
                ? []
                : [
                    {
                      key: `view_details`,
                      width: 240,
                      onClick: () => {
                        setRightPanelInfo({
                          detailType: `geoFence`,
                          detailId: `${geoFences[0].id}`,
                          detailTab: null,
                        });
                        eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.view_details);
                      },
                      content: <Text intlId="geo_fences.actions.view_details" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showExport
                ? []
                : [
                    {
                      key: `export`,
                      width: 240,
                      disabled: geoFences.length === 0,
                      onClick: () => {
                        setDialog(`export`);
                        eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.export);
                      },
                      content: <Text intlId="geo_fences.actions.export" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showEnable
                ? []
                : [
                    {
                      key: `enable`,
                      width: 240,
                      disabled: geoFences.length === 0 || (geoFences.length === 1 && Boolean(geoFences[0].enabled)),
                      onClick: () => {
                        updateGeoFences({
                          geoFences: geoFences.map((geoFence) => {
                            const updateGeoFence = GeoFenceMapper.cloneDomain(geoFence);
                            updateGeoFence.enabled = true;
                            return updateGeoFence;
                          }),
                        });
                        eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.enable);
                      },
                      content: <Text intlId="geo_fences.actions.enable" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showDisable
                ? []
                : [
                    {
                      key: `disable`,
                      width: 240,
                      disabled: geoFences.length === 0 || (geoFences.length === 1 && !geoFences[0].enabled),
                      onClick: () => {
                        updateGeoFences({
                          geoFences: geoFences.map((geoFence) => {
                            const updateGeoFence = GeoFenceMapper.cloneDomain(geoFence);
                            updateGeoFence.enabled = false;
                            return updateGeoFence;
                          }),
                        });
                        eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.disable);
                      },
                      content: <Text intlId="geo_fences.actions.disable" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showDelete
                ? []
                : [
                    null,
                    {
                      key: `delete`,
                      width: 240,
                      disabled: geoFences.length === 0,
                      onClick: () => {
                        setDialog(`delete`);
                        eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.delete);
                      },
                      content: (
                        <Text
                          intlId="geo_fences.actions.delete"
                          style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showEdit && geoFences.length === 1
                  ? [
                      {
                        key: `edit`,
                        text: <Text intlId="geo_fences.actions.edit" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          if (onEditGeoFence) {
                            onEditGeoFence(geoFences[0]);
                            eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.edit);
                          }

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                        },
                      },
                    ]
                  : []),
                ...(showViewDetails && geoFences.length === 1
                  ? [
                      {
                        key: `view_details`,
                        text: <Text intlId="geo_fences.actions.view_details" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          setRightPanelInfo({
                            detailType: `geoFence`,
                            detailId: `${geoFences[0].id}`,
                            detailTab: null,
                          });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.view_details);
                        },
                      },
                    ]
                  : []),
                ...(showExport
                  ? [
                      {
                        key: `export`,
                        disabled: geoFences.length === 0,
                        text: <Text intlId="geo_fences.actions.export" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`export`);
                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.export);
                        },
                      },
                    ]
                  : []),

                ...(!showEnable
                  ? []
                  : [
                      {
                        key: `enable`,
                        disabled: geoFences.length === 0 || (geoFences.length === 1 && Boolean(geoFences[0].enabled)),
                        text: <Text intlId="geo_fences.actions.enable" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          updateGeoFences({
                            geoFences: geoFences.map((geoFence) => {
                              const updateGeoFence = GeoFenceMapper.cloneDomain(geoFence);
                              updateGeoFence.enabled = true;
                              return updateGeoFence;
                            }),
                          });
                          eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.enable);
                        },
                      },
                    ]),
                ...(!showDisable
                  ? []
                  : [
                      {
                        key: `disable`,
                        disabled: geoFences.length === 0 || (geoFences.length === 1 && !geoFences[0].enabled),
                        text: <Text intlId="geo_fences.actions.disable" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          updateGeoFences({
                            geoFences: geoFences.map((geoFence) => {
                              const updateGeoFence = GeoFenceMapper.cloneDomain(geoFence);
                              updateGeoFence.enabled = false;
                              return updateGeoFence;
                            }),
                          });
                          eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.disable);
                        },
                      },
                    ]),

                ...(showDelete
                  ? [
                      {
                        key: `delete`,
                        disabled: geoFences.length === 0,
                        text: (
                          <Text
                            intlId="geo_fences.actions.delete"
                            style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`delete`);
                          eventsPanel.geoFenceActionsMenu(MixPanelEvents.geoFence.actions_menu.delete);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      )}

      <ConfirmDeleteDialog
        typeData="geo_fences"
        open={dialog === `delete`}
        onCloseDialog={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={geoFences}
        handleDelete={() => {
          setDialog(undefined);
          deleteGeoFences({ geoFences });
        }}
      />
    </>
  );
};
